import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Chapter,
  Body,
  Prose,
  Ledger,
  PullQuote,
  Kicker,
} from "@/components/site/Editorial";

const ease = [0.22, 1, 0.36, 1];

const TREE_ILLUSTRATION =
  "https://images.unsplash.com/photo-1547401372-0ac4589c4d3e?auto=format&fit=crop&w=1600&q=80";

export default function FamilyTreesArticle() {
  return (
    <article data-testid="family-trees-article">
      {/* Article opener — kicker + big lede + inline illustration */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pt-8 pb-14 md:pb-20">
        <div className="grid grid-cols-12 gap-8 md:gap-14 items-end">
          <div className="col-span-12 md:col-span-7">
            <Kicker chapter="Wing I" minutes={18} dateline="A living essay" />
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, ease }}
              className="mt-6 heritage-title leading-[0.98] tracking-[-0.01em]"
              style={{ fontSize: "clamp(40px, 5.4vw, 92px)" }}
            >
              A tree is not a chart.
              <br />
              <span className="font-italic-serif italic text-[color:var(--rust)]">
                It is an argument for continuity.
              </span>
            </motion.h2>
            <p className="mt-8 max-w-[52ch] text-[17px] md:text-[19px] leading-[1.65] text-[color:var(--ink-soft)] font-italic-serif italic">
              The first family tree was drawn by someone who could not bear to
              forget. Everything since — the archives, the DNA kits, the
              ancestry apps — is a variation on that first, private act of
              memory. This is a short field-guide to building one that will
              outlive you.
            </p>
          </div>

          <div className="col-span-12 md:col-span-5">
            <div className="vintage-frame">
              <div className="aspect-[4/5] overflow-hidden">
                <img src={TREE_ILLUSTRATION} alt="Ancient roots, sepia" />
              </div>
              <div className="absolute bottom-2 left-0 right-0 text-center font-italic-serif italic text-[11px] text-[color:var(--sepia)]">
                The oldest tree in the family
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Chapter 01 */}
      <Chapter n="01" title="Why we" subtitle="build trees.">
        <Prose>
          <Body>
            Every family carries a set of unfinished sentences. The uncle who
            left for the Gulf and never quite explained. The grandmother whose
            father's name nobody said out loud. A great-aunt whose photograph
            hangs in the corridor but whose life has softened into anecdote.
            Building a family tree is, at heart, a way of finishing those
            sentences — of turning shrugged half-memories into recorded fact.
          </Body>
          <Body>
            It is also, quietly, an act of citizenship in a very small
            republic. A tree tells the newest child in the family: <em>you
            are not the first arrival, and you will not be the last</em>. It
            situates a life. In an age that keeps everything and forgets
            everything, that placement is a rare gift.
          </Body>
          <PullQuote attribution="Ursula K. Le Guin">
            The unread story is not a story; it is little black marks on wood
            pulp. The unremembered ancestor is a stranger.
          </PullQuote>
        </Prose>
      </Chapter>

      {/* Chapter 02 */}
      <Chapter n="02" title="Anatomy of a" subtitle="family tree.">
        <Prose>
          <Body>
            A well-kept tree has five layers, and most amateurs stop at the
            first two. The skill is in getting all five to sit together on
            the same page without one crowding out another.
          </Body>
          <Ledger
            items={[
              "Names — the spine. Full names, birth names, married names, chosen names.",
              "Dates — birth, marriage, death. Approximate is better than blank.",
              "Places — where a person was born, where they married, where they died.",
              "Relationships — parents, spouses, children, adoptions, godparents.",
              "Stories — the anecdotes, occupations, illnesses, migrations, small talents.",
            ]}
          />
          <Body>
            The last layer — stories — is what separates a family tree from a
            spreadsheet. A tree without stories is a stationery product. A tree
            with stories is a book.
          </Body>
        </Prose>
      </Chapter>

      {/* Chapter 03 */}
      <Chapter n="03" title="Finding the" subtitle="first name.">
        <Prose>
          <Body>
            Most trees begin with an older relative and a cup of tea. Sit with
            them, phone recording, and ask the simple questions first. Where
            were you born? What was your father's father called? What did your
            mother do before she was married? Names arrive in a rush and then
            in slow drips. Write them down before the tea goes cold.
          </Body>
          <Body>
            When elders are no longer available, the record picks up. School
            registers, parish books, land deeds, ships' manifests, census
            returns, wedding invitations, passport applications, telephone
            directories — each is a doorway. Once inside, one name unlocks
            another until you find yourself, four hours later, staring at a
            great-great-grandmother you never knew existed.
          </Body>
          <PullQuote attribution="Alex Haley">
            In every conceivable manner, the family is the link to our past,
            the bridge to our future.
          </PullQuote>
        </Prose>
      </Chapter>

      {/* Chapter 04 */}
      <Chapter n="04" title="Photographs, documents," subtitle="silences.">
        <Prose>
          <Body>
            Every family archive has silences. Ancestors who were left off
            trees because they married outside the faith. Children who died
            young and whose names were spoken only in whispers. Servants and
            adoptees who lived inside the household but never made it onto the
            branches. A modern tree makes room for them. Silence is data too.
          </Body>
          <Body>
            When you scan a photograph, scan the back as well — the ink
            annotation of a mother identifying her sisters is more valuable
            than the picture itself. Store originals in acid-free sleeves. Log
            each artefact with its date, its source, and the story it carries.
            The tree grows outward from these small provenances.
          </Body>
        </Prose>
      </Chapter>

      {/* Chapter 05 */}
      <Chapter n="05" title="Seven questions to" subtitle="ask an elder.">
        <Prose>
          <Body>
            If you are visiting an older relative and have one hour, ask these
            seven. Write, do not paraphrase. The recording matters less than
            the fact that you asked at all.
          </Body>
          <Ledger
            items={[
              "What is the earliest memory you have of your grandparents?",
              "What was your parents' marriage like — how did they meet, argue, forgive?",
              "Which of our relatives left home first, and where did they go?",
              "What did our family survive — famine, war, migration, illness, poverty?",
              "What did our family celebrate — festivals, weddings, harvests, faith?",
              "What was the family's occupation, and what did they lose to leave it?",
              "Who is the relative you wish I could have met, and why?",
            ]}
          />
          <PullQuote attribution="An interviewer's rule">
            The most important sentence is the one that comes after "I've never
            told this to anyone."
          </PullQuote>
        </Prose>
      </Chapter>

      {/* Chapter 06 */}
      <Chapter n="06" title="A tree that is" subtitle="never finished.">
        <Prose>
          <Body>
            Unlike a spreadsheet, a family tree is not something you complete.
            You inherit one, you add to it, you leave it a little better
            organised than you found it, and you pass it on. Some branches
            grow, some are pruned by history, some fall away and reappear on
            the far side of the world two generations later. The best trees
            keep space for the not-yet-arrived.
          </Body>
          <Body>
            On FamilyTree.Team every member of a family archive can add,
            invite and correct — because the truest record of a family is
            written by more than one hand.
          </Body>

          <div className="mt-8 flex flex-wrap gap-4">
            <Link
              to="/app/register"
              data-testid="family-trees-article-cta"
              className="btn-wood"
            >
              Begin your family's tree <span aria-hidden>→</span>
            </Link>
            <Link to="/archive" className="btn-ghost">
              Return to the archive
            </Link>
          </div>
        </Prose>
      </Chapter>
    </article>
  );
}
